// ExplorePH3/client/src/components/VenueCard.jsx
import React, { useState } from 'react';
import { MapPin, Users, Clock, Star, CheckCircle, Flame, Snowflake } from 'lucide-react';
import { useClerk } from '@clerk/clerk-react';

const VenueCard = ({ venue, onCheckIn, showCheckInButton = true }) => {
  const { openSignIn } = useClerk();
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);

  // Determine vibe badge styling
  const getVibeBadge = (vibe) => {
    switch (vibe?.toLowerCase()) {
      case 'fire':
        return {
          text: 'Fire',
          icon: Flame,
          className: 'bg-gradient-to-r from-red-500 to-orange-500 text-white animate-pulse-fire',
          pulseClass: 'animate-pulse-fire',
        };
      case 'chill':
        return {
          text: 'Chill',
          icon: Snowflake,
          className: 'bg-gradient-to-r from-blue-500 to-teal-500 text-white animate-pulse-chill',
          pulseClass: 'animate-pulse-chill',
        };
      case 'dead':
        return {
          text: 'Dead',
          icon: Clock,
          className: 'bg-gradient-to-r from-gray-500 to-gray-700 text-white',
          pulseClass: '',
        };
      default:
        return {
          text: 'Unknown',
          icon: Star,
          className: 'bg-gradient-to-r from-gray-400 to-gray-600 text-white',
          pulseClass: '',
        };
    }
  };

  const vibeBadge = getVibeBadge(venue.current_vibe);
  const VibeIcon = vibeBadge.icon;

  const handleCheckIn = async () => {
    if (!onCheckIn) return;
    
    setIsCheckingIn(true);
    try {
      // In a real app, this would call the API
      // For now, simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Call the parent handler
      await onCheckIn(venue.id);
      setCheckedIn(true);
      
      // Reset after 3 seconds
      setTimeout(() => {
        setCheckedIn(false);
      }, 3000);
    } catch (error) {
      console.error('Check-in failed:', error);
    } finally {
      setIsCheckingIn(false);
    }
  };

  const handleCheckInClick = () => {
    // If user is not signed in, open sign in modal
    if (!window.Clerk?.user) {
      openSignIn();
      return;
    }
    
    handleCheckIn();
  };

  // Calculate distance if available
  const renderDistance = () => {
    if (!venue.distance_km) return null;
    
    if (venue.distance_km < 1) {
      return `${Math.round(venue.distance_km * 1000)}m away`;
    }
    return `${venue.distance_km.toFixed(1)}km away`;
  };

  return (
    <div className="bg-white rounded-2xl shadow-medium overflow-hidden transition-all duration-300 hover:shadow-hard active:scale-[0.99]">
      {/* Venue Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={venue.image_url || `https://images.unsplash.com/photo-1514933651103-005eec06c04b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80`}
          alt={venue.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        
        {/* Vibe Badge */}
        <div className="absolute top-3 left-3">
          <div className={`${vibeBadge.className} px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg`}>
            <VibeIcon className="w-4 h-4" />
            <span className="text-sm font-semibold">{vibeBadge.text}</span>
          </div>
        </div>
        
        {/* Distance Badge */}
        {renderDistance() && (
          <div className="absolute top-3 right-3 bg-black/70 text-white px-3 py-1.5 rounded-full text-sm font-medium backdrop-blur-sm">
            {renderDistance()}
          </div>
        )}
      </div>

      {/* Venue Info */}
      <div className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">{venue.name}</h3>
            <div className="flex items-center text-gray-600 text-sm mb-2">
              <MapPin className="w-4 h-4 mr-1" />
              <span>{venue.address || `${venue.latitude?.toFixed(4)}, ${venue.longitude?.toFixed(4)}`}</span>
            </div>
          </div>
          
          {/* Rating */}
          {venue.average_rating && (
            <div className="flex items-center bg-sand-100 text-sand-800 px-2 py-1 rounded-lg">
              <Star className="w-4 h-4 mr-1 fill-current" />
              <span className="font-bold">{venue.average_rating.toFixed(1)}</span>
            </div>
          )}
        </div>

        {/* Description */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {venue.description || 'A great place to hang out and enjoy the atmosphere.'}
        </p>

        {/* Stats */}
        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
          <div className="flex items-center">
            <Users className="w-4 h-4 mr-1" />
            <span>{venue.recent_checkins || 0} recent</span>
          </div>
          <div className="flex items-center">
            <Clock className="w-4 h-4 mr-1" />
            <span>{venue.open_hours || 'Open now'}</span>
          </div>
        </div>

        {/* Check-in Button */}
        {showCheckInButton && (
          <button
            onClick={handleCheckInClick}
            disabled={isCheckingIn || checkedIn}
            className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
              checkedIn
                ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white'
                : isCheckingIn
                ? 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-600 cursor-not-allowed'
                : 'bg-gradient-to-r from-sunset-500 to-sunset-600 text-white active:scale-95 hover:shadow-lg'
            }`}
          >
            {checkedIn ? (
              <>
                <CheckCircle className="w-5 h-5" />
                Checked In!
              </>
            ) : isCheckingIn ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Checking In...
              </>
            ) : (
              'Check In'
            )}
          </button>
        )}
      </div>

      {/* Pulse effect for vibe badge */}
      {vibeBadge.pulseClass && (
        <div className={`absolute inset-0 rounded-2xl ${vibeBadge.pulseClass} pointer-events-none`}></div>
      )}
    </div>
  );
};

export default VenueCard;